import type {ReactElement} from 'react';
import {useRef, useState} from 'react';
import {OnMyDesignSection} from '@Components/color-picker-v2/components/on-my-design-section';
import {BrandsSection} from '@Components/color-picker-v2/components/brands-section';
import {InputSection} from '@Components/color-picker-v2/components/input-section';
import {HexPicker} from '@Components/color-picker-v2/components/hex-picker';
import {ColorOptionTypeSection} from '@Components/color-picker-v2/components/color-option-type-section';
import {ColorFillPropertyType} from '@Components/color-fill';
import './color-picker-v2.scss';
import {DefaultPresetSection} from '@Components/color-picker-mobile/components/default-preset-section';
import styles from './color-picker-v2.module.scss';
import {PosterEditorTestId} from '@/test-ids/poster-editor-test-id';
import type {RGBA} from '@Utils/color.util';

export interface ColorPickerProps {
  id: string;
  className?: string;
  color: ColorStorage[];
  onMyDesign: string[];
  type?: ColorFillPropertyType;
  addDelayToOnChangeColor?: boolean;
  isGradientEnabled?: boolean;
  hideTypeSelector?: boolean;
  showSmallBrandDropdownHeader?: boolean;
  isAlphaDisabled?: boolean;
  /** When true, the solid colour swatch shows the "mixed colours" thumbnail (a multi-selection whose colours differ). */
  isColorMixed?: boolean;
  /** When true the items disagree on fill type: the type dropdown shows `--` and the panel collapses to a single colour row. */
  isTypeMixed?: boolean;

  onChangeColor(color: ColorStorage[]): void;

  onChangeEnded(color?: ColorStorage[]): void;

  onChangeType?(type: ColorFillPropertyType): void;

  onChangeColorPickerMode?(state: boolean): void;

  onAddBrandAssetClicked?(): void;

  showOnMyDesignsSection?: boolean;
  showPicker?: boolean;
  onColorSelectedFromBrands?(): void;
}

export interface ColorStorage {
  id: string;
  color: RGBA;
}

const DELAY_FOR_ON_CHANGE = 200;

export function ColorPicker({
  className = '',
  type = ColorFillPropertyType.SOLID,
  isGradientEnabled = true,
  hideTypeSelector = false,
  onChangeColorPickerMode = (): void => {},
  addDelayToOnChangeColor = false,
  showSmallBrandDropdownHeader = false,
  showOnMyDesignsSection = true,
  showPicker = true,
  isAlphaDisabled = false,
  onColorSelectedFromBrands = (): void => {},
  ...props
}: ColorPickerProps): ReactElement<any> {
  const [selectedColorId, setSelectedColorId] = useState(props.color[0].id);
  const [tempColorValue, setTempColorValue] = useState(props.color);
  const timer = useRef<number | undefined>(undefined);

  const getSelectedColor = (): RGBA => {
    let currentColor = props.color;
    if (addDelayToOnChangeColor) {
      currentColor = tempColorValue;
    }
    if (type === ColorFillPropertyType.SOLID) {
      return currentColor[0].color;
    }

    for (let index = 0; index < props.color.length; index += 1) {
      if (currentColor[index].id === selectedColorId) {
        return currentColor[index].color;
      }
    }

    return currentColor[0].color;
  };

  const getIndexOfSelectedColor = (): number => {
    if (type === ColorFillPropertyType.SOLID) {
      return 0;
    }
    return props.color.findIndex((colorItem) => {
      return colorItem.id === selectedColorId;
    });
  };

  const prepareColorFromStringToObject = (value: RGBA | null): ColorStorage[] => {
    if (value === null) {
      return [];
    }
    const indexOfSelectedColor = getIndexOfSelectedColor();
    const newColorsArray = [...props.color];
    newColorsArray[indexOfSelectedColor].color = value;
    return newColorsArray;
  };

  const fireOnChange = (color: RGBA | null): void => {
    if (color === null) {
      return;
    }
    setTempColorValue(prepareColorFromStringToObject(color));
    if (!addDelayToOnChangeColor) {
      props.onChangeColor(prepareColorFromStringToObject(color));
      return;
    }

    if (timer) {
      clearTimeout(timer.current);
    }
    timer.current = window.setTimeout(() => {
      props.onChangeColor(prepareColorFromStringToObject(color));
    }, DELAY_FOR_ON_CHANGE);
  };

  const onChangeColor = (color: RGBA): void => {
    fireOnChange(color);
  };

  const onColorSelectFromBrands = (brandColor: RGBA): void => {
    const newColorsArray = prepareColorFromStringToObject(brandColor);
    props.onChangeColor(newColorsArray);
    props.onChangeEnded(newColorsArray);
    onColorSelectedFromBrands();
  };

  const onChangeTypeHandler = (newType: ColorFillPropertyType): void => {
    if (props.onChangeType) {
      props.onChangeType(newType);
    }
  };

  const getSelectedColorId = (): string => {
    if (type === ColorFillPropertyType.SOLID) {
      return props.color[0].id;
    }
    return selectedColorId;
  };

  return (
    <div data-testid={PosterEditorTestId.COLOR_PICKER_PANEL} className={`${className} flex-v-row spacing-p-l-5 spacing-p-r-5 ${styles.container}`}>
      <ColorOptionTypeSection
        type={type}
        color={props.color}
        selectedColorId={getSelectedColorId()}
        isGradientEnabled={isGradientEnabled}
        isColorMixed={props.isColorMixed}
        isTypeMixed={props.isTypeMixed}
        hideTypeSelector={hideTypeSelector}
        onChangeSelectedColor={setSelectedColorId}
        onChangeType={onChangeTypeHandler}
      />
      <HexPicker
        color={getSelectedColor()}
        onChange={onChangeColor}
        onChangeEnded={(value: RGBA): void => {
          const colorObject = prepareColorFromStringToObject(value);
          props.onChangeEnded(colorObject);
        }}
        isAlphaDisabled={isAlphaDisabled}
      />
      <InputSection
        onChangeColorPickerMode={(state): void => {
          onChangeColorPickerMode(state);
        }}
        color={getSelectedColor()}
        onChangeColor={(color): void => {
          fireOnChange(color);
        }}
        onChangeEnded={(color): void => {
          props.onChangeEnded(prepareColorFromStringToObject(color));
        }}
        showPicker={showPicker}
        isAlphaDisabled={isAlphaDisabled}
      />
      <BrandsSection
        className="spacing-p-t-6"
        onAddBrandAssetClicked={(): void => {
          if (props.onAddBrandAssetClicked) {
            props.onAddBrandAssetClicked();
          }
        }}
        showSmallDropdownHeader={showSmallBrandDropdownHeader}
        onColorSelect={onColorSelectFromBrands}
      />
      {showOnMyDesignsSection ? (
        <OnMyDesignSection
          onColorSelect={(color): void => {
            props.onChangeColor(prepareColorFromStringToObject(color as RGBA));
            props.onChangeEnded(prepareColorFromStringToObject(color as RGBA));
          }}
          colorsOnDesign={props.onMyDesign}
        />
      ) : null}
      <DefaultPresetSection
        onChangeColor={(color): void => {
          props.onChangeColor(prepareColorFromStringToObject(color));
          props.onChangeEnded(prepareColorFromStringToObject(color));
        }}
        isMobileVariant={false}
      />
    </div>
  );
}
